// Excitation and radiation profiles for the acoustic catalog. Frequencies are
// representative voicing choices, not measured impulse responses. The string
// engine supplies dispersion and frequency-dependent losses; fixed EQ represents
// the body, while a separate per-note filter follows playing pressure/velocity.
// See compositions/PHYSICS.md for sources and the limits of this approximation.
type Settings = {[key: string]: any};
const lp = (cutoffHz: number) => ({type:"low-pass", cutoffHz, linearGain:0.7071});
const hp = (cutoffHz: number) => ({type:"high-pass", cutoffHz, linearGain:0.7071});
const mode = (cutoffHz: number, linearGain = 1.4142) => ({type:"peak", cutoffHz, linearGain});
const partials = (values: number[]) => Array.from({length:28}, (_,i) => values[i] || 0);
const size = {target:"noteVolume", envelope:"note size"};
const pressure = {target:"noteFilterFreq", index:0, envelope:"note size"};

function driven(harmonics: number[], body: any[], cutoff: number, attack: number, release = 6): Settings {
    return {type:"harmonics",volume:80,eqFilter:body.filter(p => p.type !== "high-pass" || p.cutoffHz >= 62.5),effects:["note filter","reverb"],
        reverb:33,fadeInSeconds:attack,fadeOutTicks:release,unison:"none",
        // Pressure already scales cutoff by (note size / 3)^1.5. Halving it
        // again hid upper partials even at ordinary playing strengths.
        harmonics:partials(harmonics),noteFilter:[lp(cutoff)],envelopes:[size,pressure]};
}
function string(harmonics: number[], body: any[], sustain: number, cutoff: number, release: number, unison = "none"): Settings {
    return {...driven(harmonics,body,cutoff,0,release),type:"Picked String",unison,
        stringSustain:sustain,stringSustainType:"acoustic"};
}

// A full harmonic series survives sustained bowing. The fixed body response
// makes vibrato change timbre too. No twang envelope: that would lose the upper
// partials as though the string had been plucked. Solo voices use one oscillator
// so their fundamental cannot disappear through subtractive unison beating.
const bowed: {[name:string]: [number,number,number,number,number]} = {
    violin:[150,300,500,2800,7400], fiddle:[150,300,520,3000,8200],
    viola:[100,220,400,2200,6500], cello:[40,100,220,1600,5600],
    contrabass:[25,65,160,1200,4400], strings:[65,180,450,2500,7000],
    "slow strings":[65,180,450,2200,6500], "tremolo strings":[65,180,450,2800,7400],
};
const bowedPartials = [100,100,86,86,86,71,71,71,57,57,57,57,43,43,43,29,29,29,29,14,14,14,14,14];
function excitation(settings: Settings, mode: string, texture: number, motion: number): Settings {
    return {...settings,acousticMode:mode,acousticTexture:texture,acousticMotion:motion};
}

// Bore classes deliberately differ: low clarinet emphasizes odd partials;
// conical reeds retain both odd and even; flute/ocarina are fundamental-led.
const winds: {[name:string]: [number[],number,number,number,number]} = {
    flute:[[100,71,57,43,29,14],120,0,8200,.0263],
    piccolo:[[100,71,57,43,29,14],350,0,11000,.0125],
    recorder:[[100,57,43,29,14],120,0,7000,.0125],
    whistle:[[100,29,14],200,0,9000,.0263],
    ocarina:[[100,29,14],80,0,5000,.0413],
    "pan flute":[[100,71,43,29,14],100,0,7200,.0413],
    shakuhachi:[[100,71,57,43,29,29,14],90,1400,7500,.0575],
    "blown bottle":[[100,14],60,0,3500,.0575],
    clarinet:[[100,43,86,29,71,29,57,14,43,14,29,14],65,0,6500,.0263],
    oboe:[[86,100,100,86,71,71,57,43,43,29,14],140,1400,8200,.0263],
    "english horn":[[100,100,86,86,71,57,57,43,29,14],90,950,6500,.0413],
    bassoon:[[100,100,86,71,71,57,43,43,29,14],35,500,5600,.0413],
    "soprano sax":[[100,100,86,86,71,71,57,43,43,29,14],150,1800,9000,.0263],
    "alto sax":[[100,100,86,86,71,57,57,43,29,14],90,1300,8000,.0413],
    "tenor sax":[[100,100,86,71,71,57,43,43,29,14],55,900,7000,.0413],
    "baritone sax":[[100,100,86,71,57,57,43,29,29,14],30,650,5600,.0575],
    shehnai:[[86,100,100,86,86,71,57,57,43,29,14],120,1700,8000,.0263],
};
const brass: {[name:string]: [number,number,number,number[]]} = {
    trumpet:[120,1700,10000,[100,100,100,86,86,71,71,57,57,43,29,14]],
    trombone:[45,1000,7800,[100,100,86,86,71,71,57,43,29,14]],
    tuba:[20,400,4800,[100,100,86,71,57,43,29,14]],
    "french horn":[45,750,6500,[100,86,86,71,57,43,29,14]],
    "muted trumpet":[180,2200,6500,[86,100,100,86,86,71,57,43,29,14]],
    "brass section":[55,1200,8500,[100,100,100,86,86,71,57,43,29,14]],
};

export function acousticPreset(name: string, original: Settings): Settings | null {
    const spectral: {[name:string]:string} = {violin:"violin",viola:"viola",cello:"cello",flute:"flute",clarinet:"clarinet","tenor sax":"sax"};
    if(spectral[name]) {
        // The source already contains the recorded spectral envelope/body
        // response. Do not apply the former approximate body EQ a second time.
        // Neutral harmonic sliders are an editable attenuation mask over the
        // evolving 64-partial source, not a replacement static waveform.
        const s:Settings={type:"harmonics",volume:80,eqFilter:[],effects:["note filter","reverb"],
            reverb:33,fadeInSeconds:0,fadeOutTicks:/violin|viola|cello/.test(name)?12:6,unison:"none",
            harmonics:Array(28).fill(100),noteFilter:[lp(16000)],envelopes:[size],
            acousticMode:"spectral-"+spectral[name],acousticTexture:name==="flute"?80:/violin|viola|cello/.test(name)?65:45,
            acousticMotion:/violin|viola|cello/.test(name)?55:65};
        if(name!=="clarinet"){s.effects.push("vibrato");s.vibrato="delayed";}
        return s;
    }
    if (/^(choir|solo) (soprano|tenor|bass)$/.test(name) || name === "voice ooh") {
        // Glottal excitation is harmonic; vowel formants are resonances of the
        // vocal tract, not harmonics locked to the sung fundamental. These are
        // representative open / rounded vowel colors rather than speech models.
        const soprano = name.indexOf("soprano") !== -1, bass = name.indexOf("bass") !== -1, rounded = name === "voice ooh";
        const formants = rounded ? [350,900,2400] : soprano ? [850,1400,3000] : bass ? [600,1000,2400] : [700,1200,2700];
        const s = driven([100,86,86,71,71,57,57,43,43,29,29,14,14,14],
            [hp(soprano ? 130 : 65),mode(formants[0],2),mode(formants[1],2),mode(formants[2]),lp(6500)],6500,.0575,12);
        s.effects.push("vibrato");s.vibrato="delayed";
        if (name.indexOf("choir") === 0) {s.unison="hum";s.effects.push("chorus");s.chorus=33;s.fadeInSeconds=.085;}
        return s;
    }
    if (["overdrive guitar","distortion guitar"].includes(name)) {
        const s = string([100,100,86,86,71,71,57,43,43,29,14],
            [hp(80),mode(800),lp(4800)],71,8500,6);
        s.effects.push("distortion");s.distortion=name === "overdrive guitar" ? 43 : 71;
        // Pickup/string dynamics feed the nonlinear amplifier; cabinet response
        // limits its high-frequency products without deleting the lower strings.
        return s;
    }
    if (bowed[name]) {
        const [low,air,plate,bridge,high] = bowed[name];
        const ensemble = name.indexOf("strings") !== -1;
        const h = bowedPartials.slice();
        if (/cello|contrabass/.test(name)) {h[1]=86;h[3]=71;h[5]=57;}
        if (/violin|fiddle/.test(name)) {
            // Harmonic sliders are exponential (one step is roughly 6 dB),
            // not linear percentages. Keep energy feeding the bridge region
            // instead of losing it in both the source and the pressure filter.
            h.splice(0,h.length,86,100,86,86,86,71,71,86,71,71,57,57,43,43,43,29,29,29,29,14,14,14,14,14);
        }
        // The upper bridge region and a shallow intervening valley make
        // vibrato move individual harmonics through different body gains.
        const body = /violin|fiddle/.test(name)
            ? [hp(low),mode(air,1.4142),mode(plate,1.6818),mode(1200,.7071),mode(2500,1.4142),mode(3600,1.1892),lp(high)]
            : [hp(low),mode(air,1.4142),mode(plate,1.6818),mode(Math.sqrt(plate*bridge),.7071),mode(bridge,1.6818),lp(high)];
        // Fiddle is the same instrument with a firmer, quicker bow stroke.
        // Differences must survive native filter quantization, not just JSON.
        if(name === "fiddle") {
            h[0]=71;h[2]=100;h[4]=100;h[6]=86;
            body[3]=mode(1200,1);body[4]=mode(2800,2);
        }
        const s = excitation(driven(h,body,high,name === "slow strings" ? .1575 : name === "fiddle" ? .0125 : .0413,12),/violin|fiddle/.test(name)?"bowed":"bow",ensemble?24:name==="fiddle"?46:38,ensemble?38:55);
        s.effects.push("vibrato"); s.vibrato = "delayed";
        if (ensemble) { s.unison="hum"; s.effects.push("chorus"); s.chorus=33; }
        if (name === "tremolo strings") s.envelopes.push({target:"noteVolume",envelope:"tremolo4"});
        return s;
    }
    if (winds[name]) {
        const [h,low,formant,high,attack] = winds[name];
        const airJet = /flute|piccolo|recorder|whistle|ocarina|shakuhachi|bottle/.test(name);
        const body = [hp(low),...(formant ? [mode(formant,1.6818),mode(formant*2.3,.8409)] : name==="clarinet" ? [mode(1200,1.4142),mode(2400,.8409),mode(3600,1.1892)] : airJet ? [mode(name==="piccolo"?2800:1800,1.4142)] : []),lp(high)];
        const s = excitation(driven(h,body,high,attack),airJet?"air":"reed",
            /shakuhachi|pan flute/.test(name)?52:/flute|piccolo/.test(name)?38:airJet?22:/sax/.test(name)?30:22,
            /clarinet|recorder|ocarina|bottle|whistle/.test(name)?28:45);
        if (!/clarinet|recorder|ocarina|bottle|whistle/.test(name)) { s.effects.push("vibrato"); s.vibrato="delayed"; }
        return s;
    }
    if (brass[name]) {
        const [low,bell,high,h] = brass[name];
        const s = driven(h,[hp(low),mode(bell,1.4142),lp(high)],high,.0263);
        s.effects.push("vibrato"); s.vibrato="delayed";
        if (name === "brass section") { s.unison="hum"; s.effects.push("chorus"); s.chorus=33; }
        return s;
    }
    if (["grand piano","bright piano","honky-tonk piano","electric grand"].includes(name)) {
        // Hammer contact suppresses the highest partials; a harder strike opens
        // the velocity filter. Acoustic sustain invokes register-dependent loss
        // and string dispersion in the existing delay-line engine.
        if(name === "electric grand") {
            // A close pickup emphasizes string/hammer modes without the broad
            // acoustic soundboard or the grand's paired-string beating.
            return excitation(string([100,86,100,71,71,43,57,29,43,14],
                [hp(40),mode(700,2),mode(2200,.5),lp(6500)],71,7000,9),"pluck",18,20);
        }
        const bright = name === "bright piano", honky = name === "honky-tonk piano";
        const s = string(bright ? [100,100,86,86,71,71,57,57,43,43,29,29,14,14] : [100,100,86,71,71,57,43,43,29,29,14,14],
            [hp(25),mode(180,1.4142),mode(900,1.4142),lp(bright ? 11000 : 8500)],86,bright ? 10000 : 7600,12,honky ? "honky tonk" : "piano");
        if (name === "grand piano") {
            // Bop 4 revision: a fuller, slower-rolling partial series under a
            // darker hammer filter, with a shorter sustain, reads as a felt
            // hammer on a real soundboard instead of a bright plucked string.
            s.harmonics = [100,100,100,86,86,86,71,71,71,57,57,57,57,43,43,43,43,43,29,29,29,29,29,29,14,14,14,14];
            s.noteFilter = [{type:"low-pass", cutoffHz:2828.43, linearGain:0.7071}];
            s.stringSustain = 57;
        }
        return s;
    }
    const guitars: {[name:string]: [number,number,number[],string]} = {
        "nylon guitar":[57,6500,[100,100,86,71,57,57,43,29,29,14,14],"none"],
        "steel guitar":[71,10000,[100,86,100,86,71,57,71,43,43,29,14],"none"],
        "jazz guitar":[71,4300,[100,100,86,71,57,43,29,14],"none"],
        "clean guitar":[71,8000,[100,86,86,71,57,57,43,29,29,14],"none"],
        "muted guitar":[29,4300,[100,86,71,57,43,29,14],"none"],
        "guitar harmonics":[71,8500,[100,43,29,14],"none"],
        ukulele:[29,9500,[86,100,71,86,57,71,43,29,14],"none"],
        banjo:[43,10500,[100,86,86,71,71,57,43,43,29,29,14],"none"],
        harp:[86,8500,[100,100,86,71,57,43,43,29,14],"none"],
        "pizzicato strings":[43,4000,[100,71,86,57,71,43,57,29,14],"none"],
        harpsichord:[71,10000,[100,100,86,86,71,57,57,43,29,14],"none"],
        clavinet:[43,8500,[100,100,86,71,57,57,43,29,14],"none"],
        koto:[57,10000,[100,100,86,86,71,57,43,29,29,14],"none"],
        dulcimer:[86,10000,[100,100,86,86,71,71,57,43,43,29,14],"piano"],
    };
    if (guitars[name]) {
        const [sustain,high,h,unison] = guitars[name];
        const acoustic = /nylon|steel|ukulele|harp|koto|dulcimer/.test(name);
        const body = acoustic ? [hp(name === "harp" ? 30 : 55),mode(name === "ukulele" ? 350 : 110,name === "ukulele" ? 2 : 1.4142),mode(name === "ukulele" ? 700 : 220),lp(high)]
            : name === "banjo" ? [hp(80),mode(400),mode(1800,1.4142),lp(high)]
            : name === "pizzicato strings" ? [hp(65),mode(220),mode(500,1.4142),lp(high)]
            : [hp(55),mode(/clavinet|harpsichord/.test(name) ? 1200 : 1800,1.4142),lp(high)];
        return excitation(string(h,body,sustain,high,name === "muted guitar" ? 3 : name === "harp" ? 48 : 12,unison),"pluck",name==="pizzicato strings"?42:acoustic?34:20,acoustic?45:28);
    }
    if (["acoustic bass","fingered bass","picked bass","fretless bass","slap bass 1","slap bass 2"].includes(name)) {
        // Different contact and pickup positions alter the excitation spectrum,
        // decay and onset. These were previously identical under three names.
        const profiles: {[id:string]: {h:number[],sustain:number,high:number,body:any[],texture:number,attack?:number}} = {
            "acoustic bass":{h:[100,71,86,57,43,43,29,14],sustain:57,high:3800,body:[mode(90,2),mode(220,1.4142),mode(800,.5)],texture:38},
            "fingered bass":{h:[100,86,71,57,43,29,14],sustain:71,high:5000,body:[mode(120,1.4142),mode(1800,.5)],texture:14},
            "picked bass":{h:[100,100,86,86,71,57,71,43,43,29,14],sustain:57,high:8500,body:[mode(900,1.4142),mode(2200,2)],texture:42},
            "fretless bass":{h:[100,71,100,86,57,71,43,29,14],sustain:86,high:4200,body:[mode(600,2),mode(1600,.5)],texture:7,attack:.0263},
            "slap bass 1":{h:[100,100,71,86,57,71,43,57,29,43,14],sustain:43,high:8500,body:[mode(160,2),mode(700,.5),mode(3200,2)],texture:60},
            "slap bass 2":{h:[86,100,86,57,100,71,43,71,29,43,29],sustain:29,high:10000,body:[mode(350,.5),mode(1800,2),mode(4500,2)],texture:75},
        };
        const p=profiles[name],s=excitation(string(p.h,[...p.body,lp(p.high)],p.sustain,p.high,6),"pluck",p.texture,32);
        if(p.attack)s.fadeInSeconds=p.attack;
        return s;
    }
    // Inharmonic bars, tines and bells keep their deliberately non-string FM
    // ratios. Preserve their modal decay, remove fixed legacy over-filtering,
    // and couple strike strength to upper modes as well as overall energy.
    if (["electric piano 1","electric piano 2","celesta","glockenspiel","music box 1","music box 2","vibraphone","marimba","kalimba","xylophone","tubular bell","tinkle bell","agogo","steel pan","timpani","woodblock","taiko drum","tom-tom","harmonica","accordion","bandoneon","reed organ","bagpipe","sitar","shamisen"].includes(name)) {
        const s: Settings = JSON.parse(JSON.stringify(original));
        s.effects = Array.from(new Set([...(s.effects || []),"note filter"]));
        const low = /piano|timpani|taiko|tom-tom/.test(name) ? 25 : 60;
        const high = /marimba|kalimba|woodblock/.test(name) ? 6000 : 9500;
        s.eqFilter=[...(low >= 62.5 ? [hp(low)] : []),lp(high)]; s.noteFilter=[lp(high)];
        s.envelopes=(s.envelopes || []).filter((e: any) => !/^noteFilter/.test(e.target) && !(e.target === "noteVolume" && e.envelope === "note size"));
        s.envelopes.push(size,pressure);
        if (!/harmonica|accordion|bandoneon|reed organ|bagpipe/.test(name)) {
            s.fadeInSeconds=0;
            // A bar/tine cannot sustain at constant amplitude after the mallet
            // leaves it. Some legacy FM presets only decayed the bright modes.
            if (!s.envelopes.some((e:any) => e.target === "noteVolume" && /twang|decay/.test(e.envelope))) {
                s.envelopes.push({target:"noteVolume",envelope:/woodblock|xylophone|agogo/.test(name) ? "twang 2" : "twang 3"});
            }
        }
        return s;
    }
    return null;
}
